import React, { useState, useEffect } from 'react';
import {
  Bell,
  Mail,
  Settings,
  Save,
  CheckCircle,
  AlertTriangle,
  Info,
  Clock,
  Calendar,
  Zap
} from 'lucide-react';
import { userAPI } from '../../services/api';
import Button from '../UI/Button';
import { logInfo, logError } from '../../utils/logger';

interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  deviceAlerts: boolean;
  systemUpdates: boolean;
  weeklyReports: boolean;
  criticalAlerts: boolean;
  performanceAlerts: boolean;
  securityAlerts: boolean;
  maintenanceAlerts: boolean;
  dataBackupAlerts: boolean;
  userActivityAlerts: boolean;
  ruleTriggerAlerts: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  timezone: string;
}

export const NotificationSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    deviceAlerts: true,
    systemUpdates: false,
    weeklyReports: true,
    criticalAlerts: true,
    performanceAlerts: true,
    securityAlerts: true,
    maintenanceAlerts: true,
    dataBackupAlerts: false,
    userActivityAlerts: false,
    ruleTriggerAlerts: true,
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
    timezone: 'UTC'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load settings
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await userAPI.getNotificationSettings();
      // Same defensive merge as DashboardSettingsTab — if the backend
      // returns null/undefined we keep the local defaults rather than
      // overwriting `settings` with null and crashing the tab.
      if (response?.data && typeof response.data === 'object') {
        setSettings((prev) => ({ ...prev, ...response.data }));
      }

      logInfo('NotificationSettingsTab', 'Settings loaded successfully');
    } catch (err: any) {
      logError('NotificationSettingsTab', 'Failed to load settings', err);
      setError(err.response?.data?.message || 'Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await userAPI.updateNotificationSettings(settings);

      setSuccess('Notification settings saved successfully!');
      logInfo('NotificationSettingsTab', 'Settings saved successfully');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      logError('NotificationSettingsTab', 'Failed to save settings', err);
      setError(err.response?.data?.message || 'Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key: keyof NotificationSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleTimeChange = (key: 'quietHoursStart' | 'quietHoursEnd', value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleTimezoneChange = (timezone: string) => {
    setSettings(prev => ({
      ...prev,
      timezone
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Notification Settings</h2>
          <p className="text-slate-600 mt-1">
            Configure how and when you receive notifications
          </p>
        </div>
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{success}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Notification Channels */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary-600" />
          Notification Channels
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-slate-900">Email Notifications</p>
                <p className="text-sm text-slate-600">Receive notifications via email</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('emailNotifications')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.emailNotifications ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-slate-900">Push Notifications</p>
                <p className="text-sm text-slate-600">Receive notifications on your device</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('pushNotifications')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.pushNotifications ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.pushNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-purple-600" />
              <div>
                <p className="font-medium text-slate-900">SMS Notifications</p>
                <p className="text-sm text-slate-600">Receive notifications via SMS</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('smsNotifications')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.smsNotifications ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.smsNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Notification Types */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary-600" />
          Notification Types
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div>
              <p className="font-medium text-slate-900">Asset Alerts</p>
              <p className="text-sm text-slate-600">Asset status changes</p>
            </div>
            <button
              onClick={() => handleToggle('deviceAlerts')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.deviceAlerts ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${settings.deviceAlerts ? 'translate-x-5' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div>
              <p className="font-medium text-slate-900">Critical Alerts</p>
              <p className="text-sm text-slate-600">Urgent system issues</p>
            </div>
            <button
              onClick={() => handleToggle('criticalAlerts')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.criticalAlerts ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${settings.criticalAlerts ? 'translate-x-5' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div>
              <p className="font-medium text-slate-900">Maintenance Alerts</p>
              <p className="text-sm text-slate-600">Scheduled maintenance</p>
            </div>
            <button
              onClick={() => handleToggle('maintenanceAlerts')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.maintenanceAlerts ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${settings.maintenanceAlerts ? 'translate-x-5' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div>
              <p className="font-medium text-slate-900">Security Alerts</p>
              <p className="text-sm text-slate-600">Security-related events</p>
            </div>
            <button
              onClick={() => handleToggle('securityAlerts')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.securityAlerts ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${settings.securityAlerts ? 'translate-x-5' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div>
              <p className="font-medium text-slate-900">Weekly Reports</p>
              <p className="text-sm text-slate-600">Summary reports</p>
            </div>
            <button
              onClick={() => handleToggle('weeklyReports')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.weeklyReports ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${settings.weeklyReports ? 'translate-x-5' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div>
              <p className="font-medium text-slate-900">System Updates</p>
              <p className="text-sm text-slate-600">Platform updates</p>
            </div>
            <button
              onClick={() => handleToggle('systemUpdates')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.systemUpdates ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${settings.systemUpdates ? 'translate-x-5' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Quiet Hours */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary-600" />
          Quiet Hours
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <div>
              <p className="font-medium text-slate-900">Enable Quiet Hours</p>
              <p className="text-sm text-slate-600">Pause non-critical notifications during specified hours</p>
            </div>
            <button
              onClick={() => handleToggle('quietHoursEnabled')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.quietHoursEnabled ? 'bg-secondary-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.quietHoursEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>

          {settings.quietHoursEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={settings.quietHoursStart}
                  onChange={(e) => handleTimeChange('quietHoursStart', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <input
                  type="time"
                  value={settings.quietHoursEnd}
                  onChange={(e) => handleTimeChange('quietHoursEnd', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timezone */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary-600" />
          Timezone
        </h3>

        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Timezone
          </label>
          <select
            value={settings.timezone}
            onChange={(e) => handleTimezoneChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
          >
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="Europe/London">London (GMT)</option>
            <option value="Europe/Paris">Paris (CET)</option>
            <option value="Asia/Tokyo">Tokyo (JST)</option>
            <option value="Asia/Shanghai">Shanghai (CST)</option>
            <option value="Australia/Sydney">Sydney (AEST)</option>
          </select>
          <p className="text-sm text-gray-600 mt-2">
            This affects when quiet hours and scheduled notifications are sent
          </p>
        </div>
      </div>
    </div>
  );
};
